import React, { useState } from "react";
import { CiUser } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import { CREATE_USER_FEED } from "../../apollo/operations";
import { Create, Shimmer, SkeletonImages, SkeletonPost } from "..";
import { UserFeedsType } from "../../types";
import { handleIPFSUploads } from "../../utils/iPfsUpload";
import { Link, useNavigate } from "react-router-dom";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { showToast } from "../../utils/showToast";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import Resizer from "react-image-file-resizer";
import { useAutoAdjustTextareaRows } from "../../utils/adjustScroll";
import { setUserOnlyFeeds } from "../../redux/slices/communityFeedSlice";

type UserPostInputProps = {
  refetch?: () => void;
  userFeed: UserFeedsType | undefined;
  _profileImage?: string;
  toggle: boolean | (() => void);
  isLocked: boolean | (() => void);
  loadProfile: boolean;
  UserName: string;
};
const UserPostInput: React.FC<UserPostInputProps> = ({
  refetch,
  _profileImage,
  toggle,
  isLocked,
  loadProfile,
  UserName,
}) => {
  const [post, setPost] = React.useState("");
  const [images, setImages] = React.useState<File[]>([]);
  const [imageUpload, setImageUpload] = React.useState<string[]>([]);
  const [imageUploadLoading, setImageUploadLoading] = React.useState(false);
  const [onChange, setOnChange] = React.useState(false);
  const [openCreate, setOpenCreate] = React.useState(false);
  const navigate = useNavigate();
  // const user = useSelector((state: RootState) => state.user.userDetails);
  const [isVideoCompressing, setIsVideoCompressing] = useState<boolean>(false);
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [compressedVideos, setCompressedVideos] = useState<File[]>([]);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  const [selectedPrevColor, setSelectedPrevColor] = useState<string | null>(
    null
  );
  const [thumbnail, setThumbnail] = useState<string[]>([]);
  const [thumbFile, setThumbFile] = React.useState<File[]>([]);
  const colors = [
    "#1708FF",
    "#532F2F",
    "#CE0A0A",
    "#082BA8",
    "#CEA30A",
    "#088BA8",
    "#61A808",
    "#FFE603",
    "#FF276A",
    "#9108A8",
    "#61A808",
    "#FFE603",
    "#FF276A",
    "#9108A8",
    "#92E3A9",
  ];
  const onVideoSelectFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      setOnChange(true);
      const selectedFilesArray = Array.from(selectedFiles);

      try {
        const allThumbnails: string[] = [];
        const allFileThumb: File[] = [];
        const compressedVideosArray: File[] = [];

        for (const selectedFile of selectedFilesArray) {
          const videoObjectUrl = URL.createObjectURL(selectedFile);
          const videoPlayer = document.createElement("video");
          videoPlayer.src = videoObjectUrl;

          await new Promise((resolve) => {
            videoPlayer.onloadedmetadata = () => resolve(undefined);
          });

          const duration = videoPlayer.duration;
          const maxDuration = 120;

          if (duration > maxDuration) {
            showToast(
              `Video duration exceeds the maximum allowed (${maxDuration} seconds).`,
              "warn"
            );
            setOnChange(false);
            return;
          }
          setIsVideoCompressing(true);

          const canvas = document.createElement("canvas");
          canvas.width = videoPlayer.videoWidth;
          canvas.height = videoPlayer.videoHeight;
          const ctx = canvas.getContext("2d");

          videoPlayer.currentTime = 1;

          await new Promise((resolve) => {
            videoPlayer.onseeked = () => {
              ctx?.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
              resolve(undefined);
            };
          });

          const thumbnailDataUrl = canvas.toDataURL("image/jpeg");
          const thumbnailBlob = await new Promise<Blob>((resolve) =>
            canvas.toBlob((blob) => resolve(blob as Blob), "image/jpeg")
          );

          const thumbnailFile = new File(
            [thumbnailBlob],
            `${selectedFile.name}`,
            {
              type: "image/jpeg",
              lastModified: Date.now(),
            }
          );

          allFileThumb.push(thumbnailFile);
          allThumbnails.push(thumbnailDataUrl);

          const arrayBuffer = await selectedFile.arrayBuffer();
          const worker = new FFmpeg();
          await worker.load();

          const uint8Array = new Uint8Array(arrayBuffer);
          await worker.writeFile("input.mp4", uint8Array);
          await worker.exec(["-i", "input.mp4", "-c", "copy", "output.mp4"]);

          const data = await worker.readFile("output.mp4");
          const compressedVideoBlob = new Blob([data], { type: "video/mp4" });

          const compressedVideoFile = new File(
            [compressedVideoBlob],
            `compressed_${selectedFile.name}`,
            {
              type: "video/mp4",
              lastModified: Date.now(),
            }
          );

          compressedVideosArray.push(compressedVideoFile);
          await worker.terminate();
        }

        setCompressedVideos((prevCompressedVideos) => [
          ...prevCompressedVideos,
          ...compressedVideosArray,
        ]);
        setThumbFile((thumb) => [...thumb, ...allFileThumb]);
        setThumbnail((prevThumbnail) => [...prevThumbnail, ...allThumbnails]);

        const fileToStringVideo = selectedFilesArray.map((video) =>
          URL.createObjectURL(video)
        );
        const updatedPreview = [...selectedVideos, ...fileToStringVideo];
        setSelectedVideos(updatedPreview);
        setIsVideoCompressing(false);
      } catch (error) {
        console.log(error);
        setIsVideoCompressing(false);
      }

      event.target.value = "";
    }
  };

  const handleOnChange = (e: any) => {
    const inputValue = e.target.value.trim(); // Trim whitespace from the input value
    setPost(e.target.value); // Update the post state with the input value

    if (inputValue.trim().length > 0 || imageUpload.length > 0) {
      setOnChange(true); // Enable the button if there is non-whitespace content
    } else {
      setOnChange(false); // Disable the button if the content is empty or contains only whitespace
    }
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   e.preventDefault();
  //   const selectedImages = e.target.files;

  //   if (selectedImages) {
  //     // setOnChange(true);
  //     const newImageArray = Array.from(selectedImages);
  //     const updatedImages = [...images, ...newImageArray];
  //     setImages(updatedImages);

  //     const newPreviewArray = newImageArray.map((image) =>
  //       URL.createObjectURL(image)
  //     );
  //     const updatedPreviews = [...(imageUpload || []), ...newPreviewArray];
  //     setImageUpload(updatedPreviews);
  //   }
  // };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const selectedImages = e.target.files;
    if (selectedImages) {
      setOnChange(true);
      const newImageArray = Array.from(selectedImages);

      // Resize and add images to the state
      const resizedImages: File[] = [];
      const resizedPreviews: string[] = [];

      for (const image of newImageArray) {
        await new Promise<void>((resolve) => {
          Resizer.imageFileResizer(
            image,
            700, // Width
            679, // Height
            "JPEG", // Format
            100, // Quality
            0, // Rotation
            (uri) => {
              // Resized image
              if (typeof uri === "string") {
                resizedPreviews.push(uri);
                const file = dataURLtoFile(uri, image.name);
                // console.log(file);

                resizedImages.push(file);
              }
              resolve();
            }
            // "base64", // Output type
            // 200, // Max width
            // 200 // Max height
          );
        });
      }

      // Update state with resized images and previews
      const updatedImages = [...images, ...resizedImages];
      setImages(updatedImages);

      const updatedPreviews = [...imageUpload, ...resizedPreviews];
      setImageUpload(updatedPreviews);
    }
  };

  // Function to convert data URL to File
  const dataURLtoFile = (dataUrl: string, fileName: string) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const userID = window.localStorage.getItem("user");
  const handleClick = () => {
    setOpenCreate(true);
    if (typeof toggle === "function") {
      toggle();
    }
  };
  const dispatch = useDispatch();
  const userDetails = useSelector((state: any) => state.user.userDetails);
  const userFeedOnly = useSelector(
    (state: any) => state.communityFeeds.UserFeeds
  );

  const [createPost, { loading }] = useMutation(CREATE_USER_FEED, {
    onCompleted(data) {
      // console.log(data.createUserFeed);
      dispatch(setUserOnlyFeeds([...userFeedOnly, data.createUserFeed]));

      if (refetch) {
        refetch();
      }
      setPost("");
      setImages([]);
      setImageUpload([]);
      setCompressedVideos([]);
      setSelectedColor(null);
      setSelectedPrevColor(null);
      setOnChange(false);
      setOpenCreate(false);
      navigate("/ecobook");
    },
    onError: (err) => {
      console.log(err);
      const errorMsg = err.graphQLErrors[0]?.extensions?.originalError;
      console.log(errorMsg);
      console.log("failed");
    },
  });
  useAutoAdjustTextareaRows("scrollingText");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setImageUploadLoading(true);
    if (userDetails.id) {
      setOnChange(false);
      handleIPFSUploads(images)
        .then((ipfsHashes) => {
          const uploadPromises = compressedVideos.map((video, index) => {
            return handleIPFSUploads([video, thumbFile[index]]).then(
              (hashes) => {
                if (hashes && hashes.length === 2) {
                  return `${hashes[0]} ${hashes[1]}`;
                } else {
                  throw new Error(
                    "Failed to upload video or thumbnail to IPFS"
                  );
                }
              }
            );
          });

          Promise.all(uploadPromises)
            .then((videoThumbHashes) => {
              createPost({
                variables: {
                  data: {
                    content: post,
                    media: {
                      imageUrl: ipfsHashes,
                      videoUrl: videoThumbHashes,
                    },
                    creator: {
                      id: userID,
                    },
                    feedBackground: selectedColor,
                  },
                },
              });
            })
            .catch((error) => {
              console.error("Error uploading videos or thumbnails:", error);
              showToast(
                "An error occurred while uploading videos or thumbnails.",
                "error"
              );
            });
        })
        .finally(() => {
          console.log("post feed successful");
          setImageUploadLoading(false);
          setSelectedColor(null);
          setSelectedPrevColor(null);
          if (typeof toggle === "function") {
            toggle();
          }
          // if(refetch) refetch()
        });
    }
  };

  // if (isVideoCompressing) {
  //   return null;
  // }

  return (
    <div className="flex flex-col">
      <div className="flex w-full justify-between items-center mx-auto lg:mb-4">
        <div className="h-[84px] gap-1 lg:w-[70%] pl-[4%] w-full pr-[20%] lg:shadow-sm rounded-md mx-auto items-center bg-white py-6 lg:px-8 px-1 flex justify-between">
          {loadProfile && (
            <div className="lg:h-[50px] lg:w-[50px] w-[30px] h-[30px] bg-slate-100 flex items-center justify-center rounded-full relative overflow-hidden">
              <Shimmer />
            </div>
          )}
          {_profileImage ? (
            <img
              alt=""
              src={ipfsGatewayImage(_profileImage!)}
              className="lg:h-[50px] lg:w-[50px] w-[30px] h-[30px] rounded-full"
            />
          ) : (
            !loadProfile && (
              <div className="lg:h-[50px] lg:w-[50px] w-[30px] h-[30px] bg-slate-100 flex items-center justify-center rounded-full">
                <CiUser size={20} />
              </div>
            )
          )}
          <div
            className="lg:w-[80%] w-[90%] flex items-center h-7 py-2 lg:pl-4 pl-1 text-black text-sm font-Poppins font-medium lg:text-searchCol lg:font-SegoeUI lg:font-light lg:bg-comPagBg"
            onClick={handleClick}
          >
            What would you like to share?
          </div>
        </div>

        <Link
          className="bg-midGreen hidden cursor-pointer lg:flex font-SegoeUI items-center text-white justify-center font-semibold text-sm w-[28%] h-[72px] shadow-sm rounded-md py-4 px-3"
          to="/vehicles"
        >
          Switch to Ecomoto
        </Link>
      </div>
      {(loading && (
        <SkeletonPost
          post={post}
          _profileImage={_profileImage}
          UserName={UserName}
        />
      )) ??
        (loading && images && <SkeletonImages />)}
      {openCreate && isLocked && (
        <Create
          post={post}
          handleImageChange={handleImageChange}
          handleOnChange={handleOnChange}
          handleSubmit={handleSubmit}
          imageUpload={imageUpload}
          onChange={onChange}
          setImageUpload={setImageUpload}
          imageUploading={imageUploadLoading}
          setOpenCreate={setOpenCreate}
          setPost={setPost}
          setOnChange={setOnChange}
          setImages={setImages}
          selectedVideos={selectedVideos}
          onVideoSelectFile={onVideoSelectFile}
          setSelectedVideos={setSelectedVideos}
          setCompressedVideos={setCompressedVideos}
          colors={colors}
          setSelectedColor={setSelectedColor}
          selectedColor={selectedColor}
          loading={loading}
          _pImage={_profileImage}
          images={images}
          UserName={UserName}
          toggle={toggle}
          isVideoCompressing={isVideoCompressing}
          compressedVideos={compressedVideos}
          thumbnail={thumbnail}
          setThumbnail={setThumbnail}
          selectedPrevColor={selectedPrevColor}
          setSelectedPrevColor={setSelectedPrevColor}
        />
        // <div>
        //   {thumbnail.map((thumb) => (
        //     <img src={thumb} alt="" />
        //   ))}
        // </div>
      )}
    </div>
  );
};

export default UserPostInput;
